import React from "react";
import { Link } from "react-router-dom";
import {
  Github,
  Twitter,
  Linkedin,
  FileText,
  ArrowUpRight,
} from "lucide-react";
import { Page, SectionHeading, Badge, Button } from "./ui";
import { profile, skills, experience, projects, socials } from "../data/portfolio";

const socialIcons = {
  twitter: Twitter,
  github: Github,
  linkedin: Linkedin,
};

const previewProjects = projects.slice(0, 3);

export default function Main() {
  return (
    <Page className="gap-16 sm:gap-20">
      {/* Hero */}
      <section className="space-y-5">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">
            {profile.name}
          </h1>
          <p className="text-sm text-neutral-500">{profile.nameJa}</p>
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-600 dark:text-neutral-400">
            {profile.roles.join(" · ")}
          </p>
        </div>

        {profile.available && <Badge pulse>Available for work</Badge>}

        <p className="max-w-prose text-sm leading-relaxed text-neutral-600 dark:text-neutral-400 sm:text-base">
          I love crafting{" "}
          <span className="text-neutral-900 dark:text-white">products</span> that
          tackle{" "}
          <span className="text-neutral-900 dark:text-white">real issues</span>,
          building websites and apps with intuitive{" "}
          <span className="text-neutral-900 dark:text-white">
            user experiences
          </span>{" "}
          and{" "}
          <span className="text-neutral-900 dark:text-white">
            efficient code
          </span>
          .
        </p>

        <Button href={profile.resumeUrl} external className="mt-1">
          <FileText size={16} aria-hidden="true" />
          Resume
        </Button>
      </section>

      {/* Skills */}
      <section className="space-y-4">
        <SectionHeading>Technologies and Tools</SectionHeading>
        <ul className="flex flex-wrap gap-2.5">
          {skills.map((skill) => (
            <li key={skill.name}>
              <span
                title={skill.name}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-black/10 bg-black/[0.03] p-1.5 transition-colors duration-200 hover:border-black/20 hover:bg-black/[0.06] dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/20 dark:hover:bg-white/[0.06]"
              >
                <img
                  src={skill.image}
                  alt={skill.name}
                  loading="lazy"
                  className="h-full w-full object-contain opacity-70 transition-all duration-200 hover:opacity-100 hover:grayscale-0"
                />
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Experience */}
      <section className="space-y-5">
        <SectionHeading>Experience</SectionHeading>
        <ul className="divide-y divide-black/5 dark:divide-white/5">
          {experience.map((item) => (
            <li
              key={item.company}
              className="flex flex-col gap-1 py-4 first:pt-0 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="space-y-0.5">
                <h3 className="text-sm font-medium text-neutral-900 dark:text-white sm:text-base">
                  {item.company}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{item.role}</p>
              </div>
              <span className="text-xs text-neutral-500 sm:text-sm">
                {item.period}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Projects preview */}
      <section className="space-y-5">
        <SectionHeading>Projects</SectionHeading>
        <ul className="divide-y divide-black/5 dark:divide-white/5">
          {previewProjects.map((project) => (
            <li key={project.slug} className="py-4 first:pt-0">
              <Link
                to={`/projects/${project.slug}`}
                className="group flex items-center justify-between gap-4 transition-colors duration-200"
              >
                <div className="space-y-1">
                  <h3 className="text-base font-medium text-neutral-900 dark:text-white">
                    {project.name}
                  </h3>
                  <p className="text-sm text-neutral-500">{project.summary}</p>
                </div>
                <ArrowUpRight
                  size={18}
                  aria-hidden="true"
                  className="shrink-0 text-neutral-500 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-neutral-900 dark:group-hover:text-white"
                />
              </Link>
            </li>
          ))}
        </ul>
        <Link
          to="/projects"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 transition-colors duration-200 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/30 dark:text-neutral-400 dark:hover:text-white dark:focus-visible:ring-white/40 rounded-md"
        >
          View all projects
          <ArrowUpRight size={15} aria-hidden="true" />
        </Link>
      </section>

      {/* Contact */}
      <section className="space-y-5">
        <SectionHeading>Contact</SectionHeading>
        <div className="flex flex-wrap gap-3">
          {socials.map((item) => {
            const Icon = socialIcons[item.icon];
            return (
              <Button
                key={item.name}
                href={item.link}
                external
                aria-label={`Visit ${item.name}`}
              >
                {Icon && <Icon size={16} aria-hidden="true" />}
                {item.name}
              </Button>
            );
          })}
        </div>
      </section>
    </Page>
  );
}
